/**
 * Price Match Request form
 *
 * Handles:
 * - Shopify Predictive Search product lookup
 * - selected product card state
 * - competitor URL validation
 * - competitor price sanitisation
 * - native Shopify contact form summary
 * - post-submit success/reset handling
 *
 * Keep native Shopify contact form submission.
 * Do not convert to AJAX without revisiting Shopify hCaptcha behaviour.
 */
(() => {
  const rootSelector = '[data-price-match-form]';
  const initializedAttribute = 'data-price-match-initialized';
  const modalOpenBodyClass = 'price-match-modal-open';
  const minSearchChars = 3;
  const loadingStatusMessage = 'Searching Outdoor Shop products...';
  const noResultsStatusMessage =
    'No matching Outdoor Shop products found. Try a different product name, or include the product details in the notes field.';
  const errorStatusMessage =
    'Product search is unavailable right now. You can still submit the form by adding the Outdoor Shop product name or URL in the notes field.';

  const formatPrice = (value) => {
    if (value === null || value === undefined || value === '') return '';

    if (typeof value === 'object') {
      const nested = value.price ?? value.amount ?? value.value;
      if (nested !== undefined) return formatPrice(nested);
      return '';
    }

    const raw = String(value).trim();
    if (!raw) return '';
    if (raw.includes("$")) return raw;
    if (/[A-Za-z]{3}\s*\d/.test(raw)) return raw;

    const normalized = Number(raw.replace(/,/g, ''));
    if (!Number.isFinite(normalized)) return raw;

    return `$${normalized.toFixed(2)}`;
  };

  const getImageUrl = (product) => {
    const candidates = [
      product?.image?.url,
      product?.image?.src,
      product?.featured_image?.url,
      product?.featured_image?.src,
      product?.featured_image,
      product?.featured_media?.preview_image?.src,
      product?.featured_media?.preview_image?.url,
      product?.featured_media?.preview_image,
    ];
    const image = candidates.find((item) => {
      if (typeof item === 'string') return item.length > 0;
      if (item && typeof item === 'object') {
        return Boolean(item.src || item.url);
      }
      return false;
    });
    if (!image) return '';
    if (typeof image === 'object') {
      return image.src || image.url || '';
    }
    try {
      return new URL(image, window.location.origin).href;
    } catch (error) {
      return image;
    }
  };

  const getProductUrl = (product) => {
    const value = product?.url || '';
    if (!value) return '';
    if (typeof value === 'object' && (value.url || value.src)) {
      return value.url || value.src || '';
    }
    try {
      return new URL(value, window.location.origin).href;
    } catch (error) {
      return value;
    }
  };

  const getProductPrice = (product) => {
    const value = product?.price ?? product?.price_min ?? product?.price_max ?? product?.variants?.[0]?.price ?? '';
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'string') return formatPrice(value);
    if (typeof value === 'number') {
      return Number.isFinite(value) ? `$${value.toFixed(2)}` : '';
    }
    if (typeof value === 'object') {
      const nested = value.amount ?? value.value ?? value.price;
      if (nested !== undefined) return getProductPrice({ price: nested });
    }
    return formatPrice(value);
  };

  const normalizeUrl = (value) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) return '';
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const getProducts = (data) => {
    return data?.resources?.results?.products || [];
  };

  const createResultNode = ({ product, index, sectionId, onSelect, onFocus }) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'price-match-form__result';
    button.setAttribute('role', 'option');
    button.setAttribute('aria-selected', 'false');
    button.id = `${sectionId}-result-${index}`;
    button.dataset.index = String(index);

    const media = document.createElement('div');
    media.className = 'price-match-form__result-media';

    const imageUrl = getImageUrl(product);
    if (imageUrl) {
      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = product?.title || 'Product';
      img.loading = 'lazy';
      img.width = 72;
      img.height = 72;
      media.appendChild(img);
    }

    const body = document.createElement('div');
    body.className = 'price-match-form__result-body';

    const title = document.createElement('p');
    title.className = 'price-match-form__result-title';
    title.textContent = product?.title || 'Untitled product';

    body.appendChild(title);

    const priceText = getProductPrice(product);
    if (priceText) {
      const price = document.createElement('p');
      price.className = 'price-match-form__result-price';
      price.textContent = priceText;
      body.appendChild(price);
    }

    button.appendChild(media);
    button.appendChild(body);

    button.addEventListener('click', () => onSelect(product));
    button.addEventListener('mouseenter', () => onFocus(index));
    button.addEventListener('focus', () => onFocus(index));

    return button;
  };

  const createSkeletonRow = (index) => {
    const row = document.createElement('div');
    row.className = 'price-match-form__result price-match-form__result--skeleton';
    row.setAttribute('aria-hidden', 'true');
    row.dataset.index = String(index);

    const media = document.createElement('div');
    media.className = 'price-match-form__result-media price-match-form__skeleton-media';

    const body = document.createElement('div');
    body.className = 'price-match-form__result-body';

    const title = document.createElement('span');
    title.className = 'price-match-form__skeleton-line price-match-form__skeleton-line--title';

    const price = document.createElement('span');
    price.className = 'price-match-form__skeleton-line price-match-form__skeleton-line--price';

    body.appendChild(title);
    body.appendChild(price);
    row.appendChild(media);
    row.appendChild(body);

    return row;
  };

  const createResultsMessageNode = (message, modifier) => {
    const messageNode = document.createElement('p');
    messageNode.className = 'price-match-form__results-message';
    if (modifier) {
      messageNode.classList.add(`price-match-form__results-message--${modifier}`);
    }
    messageNode.textContent = message;
    return messageNode;
  };

  const buildSummary = (state) => {
    const lines = [
      `PRICE MATCH REQUEST — ${state.selectedProduct?.title || ''}`,
      '',
      'Customer details',
      `- Name: ${state.name || ''}`,
      `- Email: ${state.email || ''}`,
    ];

    if (state.phone) {
      lines.push(`- Phone: ${state.phone}`);
    }

    lines.push(
      '',
      'Selected Outdoor Shop product',
      `- Title: ${state.selectedProduct?.title || ''}`,
      `- URL: ${state.selectedProduct?.url || ''}`,
      `- Price: ${state.selectedProduct?.price || ''}`,
      '',
      'Competitor details',
      `- URL: ${state.competitorUrl || ''}`,
      `- Price: ${formatPrice(state.competitorPrice) || ''}`,
      '',
      'Notes/message',
      state.notes || '',
      '',
      'Internal review checklist',
      '- Confirm the selected Outdoor Shop product matches the competitor page.',
      '- Confirm the competitor link is valid and accessible.',
      '- Confirm the retailer is selling to New Zealand customers.',
      '- Confirm the price is comparable for the same item and currency.',
      '- Review shipping, stock, and any notes provided by the customer.'
    );

    return lines.join('\n');
  };

  const sanitizeDecimalInputValue = (value) => {
    const raw = String(value || '');
    let sanitized = raw.replace(/[^0-9.]/g, '');
    const firstDotIndex = sanitized.indexOf('.');

    if (firstDotIndex !== -1) {
      sanitized =
        sanitized.slice(0, firstDotIndex + 1) +
        sanitized.slice(firstDotIndex + 1).replace(/\./g, '');
    }

    if (sanitized.startsWith('.')) {
      sanitized = `0${sanitized}`;
    }

    return sanitized;
  };

  const sanitizeCompetitorPriceValue = (input) => {
    if (!input) return '';
    const sanitized = sanitizeDecimalInputValue(input.value);
    if (sanitized !== input.value) {
      input.value = sanitized;
    }
    return sanitized;
  };

  const handleSearchInputKeydown = ({
    event,
    resultsList,
    activeIndex,
    setActiveResult,
    showSearchResults,
    clearSearchResults,
  }) => {
    const options = Array.from(resultsList.querySelectorAll('[role="option"]'));

    if (!options.length) {
      if (event.key === 'Escape') {
        clearSearchResults();
      }
      return activeIndex;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      showSearchResults();
      const nextIndex = setActiveResult(activeIndex + 1);
      options[nextIndex]?.focus();
      return nextIndex;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      showSearchResults();
      const nextIndex = setActiveResult(activeIndex <= 0 ? options.length - 1 : activeIndex - 1);
      options[nextIndex]?.focus();
      return nextIndex;
    }

    if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      options[activeIndex]?.click();
      return activeIndex;
    }

    if (event.key === 'Escape') {
      clearSearchResults();
    }

    return activeIndex;
  };

  const cleanupContactPostedQuery = () => {
    if (!window.history?.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('contact_posted');
    const nextUrl = url.searchParams.toString()
      ? `${url.pathname}?${url.searchParams.toString()}`
      : url.pathname;
    window.history.replaceState({}, document.title, nextUrl);
  };

  const init = (root) => {
    if (root.hasAttribute(initializedAttribute)) return;
    root.setAttribute(initializedAttribute, 'true');

    const sectionId = root.dataset.sectionId || 'price-match';
    const storageKey = `price-match-form:${sectionId}`;
    const scrollStorageKey = `${storageKey}:scroll`;
    const dom = {
      form: root.querySelector('form'),
      productSearchControl: root.querySelector('[data-product-search-control]'),
      searchInput: root.querySelector('[data-product-search]'),
      searchError: root.querySelector('[data-search-error]'),
      searchStatus: root.querySelector('[data-search-status]'),
      resultsList: root.querySelector('[data-results-list]'),
      selectedCard: root.querySelector('[data-selected-card]'),
      selectedImage: root.querySelector('[data-selected-image]'),
      selectedTitleText: root.querySelector('[data-selected-title-text]'),
      selectedPriceText: root.querySelector('[data-selected-price-text]'),
      clearButton: root.querySelector('[data-clear-selected]'),
      competitorUrlInput: root.querySelector('[data-competitor-url]'),
      competitorError: root.querySelector('[data-competitor-error]'),
      competitorPriceInput: root.querySelector('[data-competitor-price]'),
      notesInput: root.querySelector('[data-notes]'),
      summaryBody: root.querySelector('[data-summary-body]'),
      submitButton: root.querySelector('[data-submit-button]'),
      submitLabel: root.querySelector('[data-submit-label]'),
      successPanel: root.querySelector('[data-success-panel]'),
      successDialog: root.querySelector('.price-match-form__success-dialog'),
      successCloseButton: root.querySelector('[data-success-close]'),
      priceMatchHeading: root.querySelector('[data-price-match-heading]'),
    };
    const hasForm = Boolean(dom.form);
    const hasSuccessPanel = Boolean(dom.successPanel);

    if (!hasForm && !hasSuccessPanel) {
      return;
    }

    const {
      form,
      productSearchControl,
      searchInput,
      searchError,
      searchStatus,
      resultsList,
      selectedCard,
      selectedImage,
      selectedTitleText,
      selectedPriceText,
      clearButton,
      competitorUrlInput,
      competitorError,
      competitorPriceInput,
      notesInput,
      summaryBody,
      submitButton,
      submitLabel,
      successPanel,
      successDialog,
      successCloseButton,
      priceMatchHeading,
    } = dom;
    const defaultSubmitLabel = submitButton?.dataset.submitLabelDefault || 'Submit price match request';
    const loadingSubmitLabel = submitButton?.dataset.submitLabelLoading || 'Sending request...';
    const maxResults = Math.max(1, Number.parseInt(root.dataset.maxResults || '6', 10) || 6);
    const searchUrl = root.dataset.searchUrl || '/search/suggest';

    let selectedProduct = null;
    let activeIndex = -1;
    let debounceTimer = 0;
    let controller = null;
    let lastSearchTerm = '';
    let searchRequestId = 0;
    let isSubmitting = false;
    let successModalOpen = false;
    let modalScrollY = 0;
    let bodyLockStyles = null;

    // Move the fixed modal out of section layout/stacking contexts.
    if (successPanel && successPanel.parentElement !== document.body) {
      document.body.appendChild(successPanel);
    }

    const focusWithoutScroll = (element) => {
      if (!element || typeof element.focus !== 'function') return;
      const scrollX = window.scrollX || 0;
      const scrollY = window.scrollY || 0;

      try {
        element.focus({ preventScroll: true });
      } catch (error) {
        element.focus();
        window.scrollTo({ left: scrollX, top: scrollY, behavior: 'auto' });
      }
    };

    const lockPageScroll = () => {
      if (bodyLockStyles) return;

      // Fixed-body locking preserves the current scroll position while the modal is open.
      modalScrollY = window.scrollY || 0;
      bodyLockStyles = {
        position: document.body.style.position,
        top: document.body.style.top,
        left: document.body.style.left,
        right: document.body.style.right,
        width: document.body.style.width,
        overflow: document.body.style.overflow,
      };

      document.body.classList.add(modalOpenBodyClass);
      document.body.style.position = 'fixed';
      document.body.style.top = `-${modalScrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    };

    const unlockPageScroll = () => {
      if (!bodyLockStyles) {
        document.body.classList.remove(modalOpenBodyClass);
        return;
      }

      const scrollY = modalScrollY;
      document.body.classList.remove(modalOpenBodyClass);
      document.body.style.position = bodyLockStyles.position;
      document.body.style.top = bodyLockStyles.top;
      document.body.style.left = bodyLockStyles.left;
      document.body.style.right = bodyLockStyles.right;
      document.body.style.width = bodyLockStyles.width;
      document.body.style.overflow = bodyLockStyles.overflow;
      bodyLockStyles = null;
      window.scrollTo({ left: window.scrollX || 0, top: scrollY, behavior: 'auto' });
    };

    const focusSuccessReturnTarget = () => {
      if (priceMatchHeading) {
        focusWithoutScroll(priceMatchHeading);
        return;
      }

      if (root && typeof root.focus === 'function') {
        focusWithoutScroll(root);
      }
    };

    const handleSuccessModalKeydown = (event) => {
      if (!successModalOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeSuccessPanel();
        return;
      }

      if (event.key === 'Tab' && successCloseButton) {
        event.preventDefault();
        focusWithoutScroll(successCloseButton);
      }
    };

    function closeSuccessPanel() {
      if (!successPanel || successPanel.hidden) return;

      successModalOpen = false;
      successPanel.classList.remove('is-visible');
      successPanel.hidden = true;
      successPanel.setAttribute('aria-hidden', 'true');
      unlockPageScroll();
      document.removeEventListener('keydown', handleSuccessModalKeydown);
      focusSuccessReturnTarget();
    }

    const revealSuccessPanel = ({ animate = true, focus = true } = {}) => {
      if (!successPanel) return;

      successModalOpen = true;
      successPanel.hidden = false;
      successPanel.removeAttribute('aria-hidden');
      successPanel.classList.remove('is-visible');
      lockPageScroll();
      document.addEventListener('keydown', handleSuccessModalKeydown);

      const activate = () => {
        successPanel.classList.add('is-visible');
        if (focus) {
          focusWithoutScroll(successCloseButton || successDialog || successPanel);
        }
      };

      if (!animate) {
        activate();
        return;
      }

      window.requestAnimationFrame(activate);
    };

    const defaultState = {
      name: '',
      email: '',
      phone: '',
      competitorUrl: '',
      competitorPrice: '',
      notes: '',
      searchTerm: '',
      selectedProduct: null,
    };

    const setFieldError = (input, errorElement, message) => {
      if (!input || !errorElement) return;
      const hasError = Boolean(message);
      input.setAttribute('aria-invalid', hasError ? 'true' : 'false');
      errorElement.hidden = !hasError;
      errorElement.textContent = message || '';
    };

    const getContactValue = (fieldName) => form.querySelector(`[name="${fieldName}"]`)?.value.trim() || '';

    const setContactValue = (fieldName, value) => {
      const input = form.querySelector(`[name="${fieldName}"]`);
      if (input) input.value = value || '';
    };

    const getCurrentFormState = () => ({
      name: getContactValue('contact[name]'),
      email: getContactValue('contact[email]'),
      phone: getContactValue('contact[phone]'),
      competitorUrl: competitorUrlInput?.value.trim() || '',
      competitorPrice: competitorPriceInput?.value.trim() || '',
      notes: notesInput?.value.trim() || '',
      searchTerm: searchInput?.value || '',
      selectedProduct,
    });

    const clearSearchResults = () => {
      cancelPendingSearch();
      resultsList.replaceChildren();
      resultsList.hidden = true;
      resultsList.setAttribute('role', 'listbox');
      resultsList.setAttribute('aria-busy', 'false');
      searchInput.setAttribute('aria-expanded', 'false');
      searchInput.removeAttribute('aria-activedescendant');
      activeIndex = -1;
    };

    const closeProductSearchUi = () => {
      if (!searchInput || !resultsList) return;
      const scrollX = window.scrollX || 0;
      const scrollY = window.scrollY || 0;
      clearSearchResults();
      productSearchControl?.classList.remove('is-active', 'is-open');
      resultsList.classList.remove('is-active', 'is-open');
      searchInput.blur();
      window.scrollTo({ left: scrollX, top: scrollY, behavior: 'auto' });
    };

    const setSearchStatus = (message) => {
      if (searchStatus) searchStatus.textContent = message || '';
    };

    const cancelPendingSearch = () => {
      if (controller) controller.abort();
      controller = null;
      searchRequestId += 1;
    };

    const showSearchResults = () => {
      resultsList.hidden = false;
      searchInput.setAttribute('aria-expanded', 'true');
    };

    const setResultsState = ({ message, busy }) => {
      resultsList.setAttribute('role', 'status');
      resultsList.setAttribute('aria-busy', busy ? 'true' : 'false');
      activeIndex = -1;
      searchInput.removeAttribute('aria-activedescendant');
      showSearchResults();
      setSearchStatus(message);
    };

    const renderLoadingState = () => {
      resultsList.replaceChildren(
        createSkeletonRow(0),
        createSkeletonRow(1),
        createSkeletonRow(2)
      );
      setResultsState({ busy: true, message: loadingStatusMessage });
    };

    const renderNoResultsState = () => {
      resultsList.replaceChildren(createResultsMessageNode(noResultsStatusMessage, 'empty'));
      setResultsState({ busy: false, message: noResultsStatusMessage });
    };

    const renderSearchErrorState = () => {
      resultsList.replaceChildren(createResultsMessageNode(errorStatusMessage, 'error'));
      setResultsState({ busy: false, message: errorStatusMessage });
    };

    const syncHiddenFields = () => {
      if (summaryBody) {
        summaryBody.value = buildSummary(getCurrentFormState());
      }
    };

    const readState = () => {
      try {
        const raw = window.sessionStorage.getItem(storageKey);
        if (!raw) return null;
        return { ...defaultState, ...JSON.parse(raw) };
      } catch (error) {
        return null;
      }
    };

    const saveState = () => {
      try {
        // SessionStorage draft state.
        window.sessionStorage.setItem(storageKey, JSON.stringify(getCurrentFormState()));
      } catch (error) {
        // Ignore storage errors.
      }
    };

    const clearState = () => {
      try {
        window.sessionStorage.removeItem(storageKey);
      } catch (error) {
        // Ignore storage errors.
      }
    };

    const saveScrollPosition = () => {
      try {
        window.sessionStorage.setItem(
          scrollStorageKey,
          JSON.stringify({ x: window.scrollX || 0, y: window.scrollY || 0 })
        );
      } catch (error) {
        // Ignore storage errors.
      }
    };

    const restoreSavedScrollPosition = ({ clear = true } = {}) => {
      try {
        const raw = window.sessionStorage.getItem(scrollStorageKey);
        if (!raw) return;
        const { x = 0, y = 0 } = JSON.parse(raw);
        if (clear) window.sessionStorage.removeItem(scrollStorageKey);
        window.scrollTo({ left: x, top: y, behavior: 'auto' });
      } catch (error) {
        // Ignore storage errors.
      }
    };

    const updateSelectedCard = () => {
      const hasSelectedProduct = Boolean(selectedProduct);
      if (productSearchControl) productSearchControl.hidden = hasSelectedProduct;
      if (hasSelectedProduct) clearSearchResults();
      if (!selectedCard) return;

      if (!hasSelectedProduct) {
        selectedCard.hidden = true;
        return;
      }

      selectedCard.hidden = false;
      if (selectedImage) {
        if (selectedProduct.imageUrl) {
          selectedImage.src = selectedProduct.imageUrl;
          selectedImage.alt = selectedProduct.title;
        } else {
          selectedImage.removeAttribute('src');
          selectedImage.alt = selectedProduct.title;
        }
      }

      if (selectedTitleText) selectedTitleText.textContent = selectedProduct.title || '';
      if (selectedPriceText) {
        const priceText = selectedProduct.price || '';
        selectedPriceText.hidden = !priceText;
        selectedPriceText.textContent = priceText;
      }
    };

    const clearSelectedProduct = (preserveSearchValue = true) => {
      selectedProduct = null;
      if (!preserveSearchValue && searchInput) {
        searchInput.value = '';
      }
      updateSelectedCard();
      syncHiddenFields();
      saveState();
    };

    const setSelectedProduct = (product) => {
      selectedProduct = {
        title: product?.title || '',
        url: getProductUrl(product),
        imageUrl: getImageUrl(product),
        price: getProductPrice(product),
      };
      if (searchInput) searchInput.value = selectedProduct.title;
      updateSelectedCard();
      clearSearchResults();
      setFieldError(searchInput, searchError, '');
      setSearchStatus(selectedProduct.title ? `Selected ${selectedProduct.title}.` : '');
      syncHiddenFields();
      saveState();
    };

    const setActiveResult = (index) => {
      const items = Array.from(resultsList.querySelectorAll('[role="option"]'));
      if (!items.length) return activeIndex;

      activeIndex = ((index % items.length) + items.length) % items.length;
      items.forEach((item, itemIndex) => {
        const isActive = itemIndex === activeIndex;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      const activeItem = items[activeIndex];
      if (activeItem) {
        searchInput.setAttribute('aria-activedescendant', activeItem.id);
      }

      return activeIndex;
    };

    const renderProducts = (products) => {
      resultsList.replaceChildren();
      resultsList.setAttribute('role', 'listbox');
      resultsList.setAttribute('aria-busy', 'false');

      const fragment = document.createDocumentFragment();
      products.slice(0, maxResults).forEach((product, index) => {
        fragment.appendChild(
          createResultNode({
            product,
            index,
            sectionId,
            onSelect: setSelectedProduct,
            onFocus: setActiveResult,
          })
        );
      });

      resultsList.appendChild(fragment);
      showSearchResults();
      setActiveResult(0);
      setSearchStatus(`${products.length} product${products.length === 1 ? '' : 's'} available.`);
    };

    const fetchProducts = async (searchTerm) => {
      if (controller) controller.abort();
      controller = new AbortController();

      const url = new URL(searchUrl, window.location.origin);
      // Predictive Search can search SKU/barcode when fields are included here.
      // Shopify may not return variant SKU/barcode in the response; they only need to be searchable.
      const searchableFields = [
        'title',
        'product_type',
        'variants.title',
        'vendor',
        'variants.sku',
        'variants.barcode',
      ].join(',');
      if (!url.pathname.endsWith('.json')) {
        url.pathname = `${url.pathname.replace(/\/$/, '')}.json`;
      }
      url.searchParams.set('q', searchTerm);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', String(maxResults));
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][fields]', searchableFields);

      setSearchStatus('Searching products...');

      const response = await fetch(url.toString(), {
        signal: controller.signal,
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Predictive search request failed: ${response.status}`);
      }

      const data = await response.json();
      return getProducts(data);
    };

    const handleSearch = () => {
      const term = searchInput.value.trim();
      lastSearchTerm = term;

      if (selectedProduct && term !== selectedProduct.title) {
        clearSelectedProduct(true);
      }

      setFieldError(searchInput, searchError, '');
      window.clearTimeout(debounceTimer);

      if (term.length < minSearchChars) {
        if (term.length === 0) {
          clearSearchResults();
          setSearchStatus(`Type at least ${minSearchChars} characters to search products.`);
        } else {
          clearSearchResults();
          setSearchStatus(`Keep typing. Enter at least ${minSearchChars} characters to search products.`);
        }
        return;
      }

      debounceTimer = window.setTimeout(async () => {
        const requestId = ++searchRequestId;
        renderLoadingState();
        try {
          const products = await fetchProducts(term);
          // Ignore stale responses from earlier requests.
          if (requestId !== searchRequestId || term !== lastSearchTerm) return;

          if (!products.length) {
            renderNoResultsState();
            return;
          }

          renderProducts(products);
        } catch (error) {
          if (error?.name === 'AbortError' || requestId !== searchRequestId) return;
          renderSearchErrorState();
        }
      }, 250);
    };

    const validateCompetitorUrl = () => {
      const rawValue = competitorUrlInput.value.trim();

      if (!rawValue) {
        setFieldError(competitorUrlInput, competitorError, 'Please enter a valid competitor website URL.');
        return false;
      }

      const normalizedValue = normalizeUrl(rawValue);

      try {
        const parsedUrl = new URL(normalizedValue);
        if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
          throw new Error('Invalid protocol');
        }

        competitorUrlInput.value = normalizedValue;
        setFieldError(competitorUrlInput, competitorError, '');
        return true;
      } catch (error) {
        setFieldError(competitorUrlInput, competitorError, 'Please enter a valid competitor website URL.');
        return false;
      }
    };

    const setSubmitState = (submitting) => {
      if (!submitButton) return;

      isSubmitting = submitting;
      submitButton.disabled = submitting;
      submitButton.setAttribute('aria-busy', submitting ? 'true' : 'false');

      if (submitLabel) {
        submitLabel.textContent = submitting ? loadingSubmitLabel : defaultSubmitLabel;
      } else {
        submitButton.textContent = submitting ? loadingSubmitLabel : defaultSubmitLabel;
      }
    };

    const resetTransientUiState = () => {
      isSubmitting = false;
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
      }
      if (submitLabel) {
        submitLabel.textContent = defaultSubmitLabel;
      }
    };

    const resetAfterSuccessfulSubmit = () => {
      form.reset();
      if (searchInput) searchInput.value = '';
      if (competitorUrlInput) competitorUrlInput.value = '';
      if (competitorPriceInput) competitorPriceInput.value = '';
      if (notesInput) notesInput.value = '';
      selectedProduct = null;
      updateSelectedCard();
      clearSearchResults();
      setFieldError(searchInput, searchError, '');
      setFieldError(competitorUrlInput, competitorError, '');
      syncHiddenFields();
      clearState();
      resetTransientUiState();
      lastSearchTerm = '';
      setSearchStatus(`Type at least ${minSearchChars} characters to search products.`);
    };

    const handleSubmit = (event) => {
      if (isSubmitting) {
        event.preventDefault();
        return;
      }

      syncHiddenFields();

      const hasSelectedProduct = Boolean(selectedProduct && selectedProduct.title);
      const competitorValid = validateCompetitorUrl();

      if (!hasSelectedProduct) {
        event.preventDefault();
        setFieldError(searchInput, searchError, 'Please select an Outdoor Shop product from the results.');
        searchInput.focus();
        return;
      }

      if (!competitorValid) {
        event.preventDefault();
        resetTransientUiState();
        competitorUrlInput.focus();
        return;
      }

      if (competitorPriceInput) {
        sanitizeCompetitorPriceValue(competitorPriceInput);
      }

      setFieldError(searchInput, searchError, '');
      setFieldError(competitorUrlInput, competitorError, '');
      syncHiddenFields();
      saveScrollPosition();
      closeProductSearchUi();
      saveState();
      setSubmitState(true);
    };

    const restoreState = () => {
      resetTransientUiState();
      const saved = readState();
      if (!saved) return;

      setContactValue('contact[name]', saved.name || '');
      setContactValue('contact[email]', saved.email || '');
      setContactValue('contact[phone]', saved.phone || '');
      if (competitorUrlInput) competitorUrlInput.value = saved.competitorUrl || '';
      if (competitorPriceInput) competitorPriceInput.value = sanitizeDecimalInputValue(saved.competitorPrice || '');
      if (notesInput) notesInput.value = saved.notes || '';
      if (searchInput) searchInput.value = saved.searchTerm || '';

      if (saved.selectedProduct?.title) {
        selectedProduct = saved.selectedProduct;
        updateSelectedCard();
      }

      syncHiddenFields();
    };

    const postedViaQuery = new URLSearchParams(window.location.search).get('contact_posted') === 'true';
    const postedSuccess = root.dataset.postedSuccess === 'true' || postedViaQuery;

    if (successCloseButton) {
      successCloseButton.addEventListener('click', closeSuccessPanel);
    }

    if (postedSuccess) {
      // Native Shopify contact form success handling.
      restoreSavedScrollPosition({ clear: false });
      if (hasForm) {
        closeProductSearchUi();
      }
      if (postedViaQuery && window.history?.replaceState) {
        cleanupContactPostedQuery();
      }
      restoreSavedScrollPosition();
      revealSuccessPanel({ animate: false, focus: true });
      if (hasForm) {
        resetAfterSuccessfulSubmit();
      }
    } else if (hasForm) {
      restoreState();
    }

    if (hasForm) {
      if (form) form.addEventListener('submit', handleSubmit);
      if (searchInput) {
        searchInput.addEventListener('input', handleSearch);
        searchInput.addEventListener('focus', () => {
          const term = searchInput.value.trim();
          if (term.length >= minSearchChars && resultsList.childElementCount > 0) {
            showSearchResults();
          }
        });
        searchInput.addEventListener('keydown', (event) => {
          activeIndex = handleSearchInputKeydown({
            event,
            resultsList,
            activeIndex,
            setActiveResult,
            showSearchResults,
            clearSearchResults,
          });
        });
        searchInput.addEventListener('blur', () => {
          window.setTimeout(() => {
            if (!root.contains(document.activeElement)) {
              clearSearchResults();
            }
          }, 100);
        });
      }

      if (competitorUrlInput) {
        competitorUrlInput.addEventListener('input', () => {
          setFieldError(competitorUrlInput, competitorError, '');
          saveState();
        });
        competitorUrlInput.addEventListener('blur', validateCompetitorUrl);
      }

      if (competitorPriceInput) {
        competitorPriceInput.addEventListener('input', () => {
          sanitizeCompetitorPriceValue(competitorPriceInput);
          saveState();
        });
        competitorPriceInput.addEventListener('blur', () => {
          sanitizeCompetitorPriceValue(competitorPriceInput);
          saveState();
        });
      }
      if (notesInput) notesInput.addEventListener('input', saveState);
      if (clearButton) {
        clearButton.addEventListener('click', () => {
          clearSelectedProduct(false);
          searchInput.focus();
        });
      }

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          saveState();
        }
      });

      syncHiddenFields();
      setSearchStatus(`Type at least ${minSearchChars} characters to search products.`);
    }
  };

  const boot = () => {
    document.querySelectorAll(rootSelector).forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
